import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { yupResolver } from '~/helpers/yupResolver';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { AppContext } from '~/App';
import { Input } from '~/components/form/Input';
import { useChangeEmailMutation } from '~/generated/graphql';
import { Page } from '../user';

type Props = {
  setShowPage: (page: Page) => void;
};

type FormData = {
  email: string;
};

const VALIDATION_SCHEMA = Yup.object().shape({
  email: Yup.string().email('This is not a valid Email').required('Required'),
});

export const UpdateEmailForm = ({ setShowPage }: Props) => {
  const { user: currentUser } = useContext(AppContext);

  const { t } = useTranslation();

  const [, changeEmail] = useChangeEmailMutation();
  const [newEmail, setNewEmail] = useState<string | null>(
    currentUser?.newEmail ?? null
  );

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormData>({
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const submit = async (values: FormData) => {
    const result = await changeEmail({ input: { email: values.email } });
    if (!result.error) {
      setNewEmail(values.email);
    }
  };

  return (
    <div className='flex w-full grow flex-col'>
      <div>
        <h2 className='text-md font-medium'>Current Email</h2>
        <p className='pt-2 text-copy-alt'>
          Your current email is{' '}
          <span className='font-medium text-black'>{currentUser?.email}</span>
          {currentUser?.emailVerified ? (
            <span className='ml-2 inline-flex items-center gap-1 rounded-full bg-success pl-1 pr-2 font-medium text-white'>
              <CheckCircleIcon className='h-4 w-4' />
              Verified
            </span>
          ) : (
            <span className='ml-2 inline-flex items-center gap-1 rounded-full bg-orangeAlert pl-1 pr-2 font-medium text-white'>
              <XCircleIcon className='h-4 w-4' />
              Not Verified
            </span>
          )}
        </p>
        {newEmail === null ? (
          <form onSubmit={handleSubmit(submit)} className='pt-8'>
            <div>
              <Input
                {...register('email')}
                label={t('newEmail')}
                type='text'
                error={errors.email?.message}
              />
            </div>
            <button
              className='mt-auto rounded-lg border border-brand px-8 py-4 text-base font-semibold leading-4 text-brand transition-all duration-300 hover:border-slate-dark hover:text-slate-dark  disabled:opacity-50'
              disabled={isSubmitting}
            >
              {t('saveChanges')}
            </button>
          </form>
        ) : (
          <div className='mt-10 bg-blueAlert bg-opacity-20 px-8 py-6'>
            <div className='flex items-center gap-4 pb-2'>
              <div className='flex h-7 w-7 items-center justify-center rounded-full bg-blue font-medium text-white'>
                i
              </div>
              <p className='font-medium'>Update pending verification</p>
            </div>
            <p className='text-copy-alt'>
              We just need to verify your email{' '}
              <span className='font-medium text-black'>{newEmail}</span>. Check
              your email to complete the update.
            </p>
            {/*
            <div className='flex gap-5 pt-2'>
              <button className='text-blue hover:text-brand cursor-pointer font-medium underline transition-all duration-300'>
                Resend Email
              </button>
              <button
                onClick={() => setHasSubmited(false)}
                className='text-blue hover:text-brand cursor-pointer font-medium underline transition-all duration-300'
              >
                Undo Change
              </button>
            </div>
            */}
          </div>
        )}

        <h2 className='text-md pt-10 font-medium'>Email Notifications</h2>
        <p className='pt-2 text-copy-alt'>
          To manage what emails you receive, visit the {''}
          <span
            onClick={() => setShowPage('notifications')}
            className='cursor-pointer font-medium text-blue underline transition-all duration-300 hover:text-brand'
          >
            notification preferences
          </span>
        </p>
      </div>
    </div>
  );
};
